import React, {useEffect, useState} from "react";
import {UserIcon} from "@heroicons/react/24/solid";
import styles from "./EditAdminClientInfo.module.css";
import Button from "../../../common/button/Button";
import {GetAdminClientInfoResponseData, UpdateClientRequest} from "../../../../types/admin";
import {getClientInfoAPI, updateClientAPI} from "../../../../services/admin";
import {useNavigate, useParams} from "react-router-dom";
import AdminLayout from "../../layout/AdminLayout";
import {useAlert} from "../../../common/error/AlertContext";
import {getRegisterAPI} from "../../../../services/term";
import OptionalPopUp from "../../../common/term/OptionalPopUp";

// 어드민 클라이언트 정보 수정 페이지, 이메일만 수정 가능
const EditAdminClientInfo: React.FC = () => {
    const navigate = useNavigate();
    const {showAlert} = useAlert();
    const {id} = useParams<{ id: string }>();
    const [data, setData] = useState<GetAdminClientInfoResponseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState<UpdateClientRequest>({
        clientId: 0,
        managerName: '',
        phoneNumber: '',
        email: '',
        department: '',
        position: ''
    });

    //이메일 형식 확인
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    //전화번호 형식 확인
    const isValidPhoneNumber = (phone: string) => {
        // 한국 휴대폰 번호 패턴: 010-1234-5678, 010.1234.5678, 01012345678
        const phoneRegex = /^(010|070|011|016|017|018|019)[-.]?\d{3,4}[-.]?\d{4}$/;
        return phoneRegex.test(phone);
    };

    //전화번호 형식화 (자동으로 하이픈 추가)
    const formatPhoneNumber = (phone: string) => {
        // 숫자만 추출
        const numbers = phone.replace(/[^\d]/g, '');

        // 010으로 시작하는 경우
        if (numbers.startsWith('010') || numbers.startsWith('070')) {
            if (numbers.length <= 3) return numbers;
            if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
        }

        // 기타 번호 (011, 016, 017, 018, 019)
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    //전화번호 유효성 검사
    const validatePhoneNumber = (phone: string) => {
        if (!phone.trim()) {
            return '전화번호를 입력해주세요.';
        }

        const numbers = phone.replace(/[^\d]/g, '');

        // 길이 검사
        if (numbers.length < 10 || numbers.length > 11) {
            return '전화번호는 10-11자리 숫자여야 합니다.';
        }

        // 유효한 시작 번호 검사
        if (!['010', '011', '016', '017', '018', '019', '070'].some(prefix => numbers.startsWith(prefix))) {
            return '유효하지 않은 전화번호 형식입니다.';
        }

        // 전체 형식 검사
        if (!isValidPhoneNumber(phone)) {
            return '올바른 전화번호 형식이 아닙니다.';
        }

        return null;
    };

    const fetchData = async () => {
        if (!id) return;
        try {
            setLoading(true);
            // 어드민 클라이언트 정보 조회 API
            const response = await getClientInfoAPI(parseInt(id));
            setData(response);
            setFormData({
                clientId: response.clientId,
                managerName: response.managerName,
                phoneNumber: formatPhoneNumber(response.phoneNumber || ''), // 기존 전화번호도 포맷팅
                email: response.email,
                department: response.department,
                position: response.position
            });
        } catch (error) {
            console.error('Error fetching account:', error);
            navigate('/admin/clients/account');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    // 전화번호 입력 핸들러
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedPhone = formatPhoneNumber(e.target.value);
        setFormData(prev => ({
            ...prev,
            phoneNumber: formattedPhone
        }));
    };

    const handleSubmit = async () => {
        // 이메일 유효성 검사
        if (!isValidEmail(formData.email)) {
            showAlert('올바른 이메일 형식이 아닙니다.', "error");
            return;
        }

        // 전화번호 유효성 검사
        const phoneError = validatePhoneNumber(formData.phoneNumber);
        if (phoneError) {
            showAlert(phoneError, "error");
            return;
        }

        try {
            // 서버로는 전화번호 숫자만 전송
            const submitData = {
                ...formData,
                phoneNumber: formData.phoneNumber
            };

            // 어드민 클라이언트 정보 수정 API
            await updateClientAPI(submitData);
            showAlert('수정되었습니다.', "info");
            // 조회 페이지로 이동
            navigate(`/admin/clients/account/${id}`);
        } catch (error) {
            console.error('Error updating client:', error);
            showAlert('수정 중 오류가 발생했습니다.', "error");
        }
    };

    return (
        <AdminLayout>
            <div className={styles.container}>
                <div className={styles.accountCard}>
                    <div className={styles.header}>
                        <div className={styles.headerContent}>
                            <div className={styles.titleSection}>
                                <UserIcon className={styles.userIcon}/>
                                <h1 className={styles.title}>계정 정보 수정</h1>
                            </div>
                            <div className={styles.buttons}>
                                {/*저장 버튼*/}
                                <Button onClick={handleSubmit} variant="primary">
                                    저장
                                </Button>
                                {/*취소 버튼*/}
                                <Button onClick={() => navigate(`/admin/clients/account/${id}`)} variant="primary">
                                    취소
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.content}>
                        <div className={styles.gridContainer}>
                            <h3 className={styles.info}>담당자 정보</h3>
                            <div className={styles.line}/>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 성명</span>
                                    {/*담당자 성명 입력*/}
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={formData.managerName}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            managerName: e.target.value
                                        }))}
                                        placeholder="담당자 성명을 입력하세요"
                                    />
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 전화번호</span>
                                    {/*전화번호 입력*/}
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={formData.phoneNumber}
                                        onChange={handlePhoneChange}
                                        placeholder="010-1234-5678"
                                    />
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>Email</span>
                                    {/*이메일 입력*/}
                                    <input
                                        type="email"
                                        className={styles.input}
                                        value={formData.email}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            email: e.target.value
                                        }))}
                                        placeholder="example@company.com"
                                    />
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 부서</span>
                                    {/*담당자 부서 입력*/}
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={formData.department}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            department: e.target.value
                                        }))}
                                        placeholder="부서명을 입력하세요"
                                    />
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.label}>담당자 직위</span>
                                    {/*담당자 직위 입력*/}
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={formData.position}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            position: e.target.value
                                        }))}
                                        placeholder="직위를 입력하세요"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default EditAdminClientInfo;